import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { fetchInstrumentGroups } from 'src/store/instrumentgroups/instrumentgroups.actions';
import {
  InstrumentDetailDto,
  InstrumentDto
} from 'src/store/instruments/instrument';
import { mapInstrumentDetailToEdit } from 'src/store/instruments/instrument.map';
import {
  createInstrument,
  deleteInstrument,
  editInstrument,
  fetchInstruments
} from 'src/store/instruments/instruments.actions';
import { InstrumentApi } from 'src/store/instruments/instruments.api';
import { selectInstruments } from 'src/store/instruments/instruments.selectors';
import { fetchMethods } from 'src/store/methods/methods.actions';
import { isLoadingInstruments } from 'src/store/requests/requests.selectors';
import { InstrumentForm } from './InstrumentForm';

export const InstrumentsPage = () => {
  const [currentInstrument, setCurrentInstrument] =
    useState<InstrumentDto | null>(null);
  const [instrument, setInstrument] =
    useState<Nullable<InstrumentDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const instruments = useSelector(selectInstruments);
  const loadingInstruments = useSelector(isLoadingInstruments);

  useEffect(() => {
    dispatch(fetchInstrumentGroups());
    dispatch(fetchMethods());
    dispatch(fetchInstruments());
  }, [dispatch]);

  useEffect(() => {
    if (currentInstrument) {
      setShowDeleteModal(true);
    }
  }, [currentInstrument]);

  const onCancel = () => {
    setCurrentInstrument(null);
    setInstrument(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (instrument: InstrumentDto) => {
    const response = await InstrumentApi.getById(instrument.id);
    if (response.ok) {
      setInstrument(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (instrument: InstrumentDto) => {
    setCurrentInstrument(instrument);
  };

  const onConfirmDelete = () => {
    if (currentInstrument) {
      dispatch(deleteInstrument(currentInstrument.id, currentInstrument));
      setCurrentInstrument(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (instrument: InstrumentDetailDto) => {
    const dto = mapInstrumentDetailToEdit(instrument);
    if (dto.id) {
      await dispatch(editInstrument(dto.id, dto));
    } else {
      await dispatch(createInstrument(dto));
    }
    dispatch(fetchInstruments());
    onCancel();
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('instrument.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('instrument.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('instrument.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={instruments}
        loading={loadingInstruments}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'name', dir: 'asc' }]}
      >
        <Column
          field="name"
          width={300}
          title={t('props.name')}
          footerCell={() => (
            <GridCountCell
              count={instruments?.length}
              label={t('instrument.plural')}
            />
          )}
        />
        <Column field="description" title={t('props.description')} />
        <Column
          field="instrumentGroup.name"
          title={t('instrumentGroup.singular')}
        />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={1000}
        header={`${instrument ? t('edit') : t('create.verb')} ${t(
          'instrument.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <InstrumentForm
          instrument={instrument}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('instrument.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('instrument.delete')} <q>{currentInstrument?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
